import { useEffect, useState } from "react"
import { Box, Stack } from "@mui/material"
import { skipToken } from "@reduxjs/toolkit/query"
import equal from "fast-deep-equal"
import type {
  Page,
  PublicationsQuery,
  TemplateVersion,
} from "../../interface/publications"
import {
  useGetTemplateQuery,
  useGetTemplatesQuery,
  useGetTemplateVersionQuery,
  useGetTemplateVersionsQuery,
} from "../../services/publications/templatesApi"
import { ResourceSelect } from "../common/resourceSelect"
import { TemplateQueryFeedback } from "../templates/templateQueryFeedback"

type Filters = Pick<PublicationsQuery, "templateId" | "templateVersionId">
const fieldSx = { width: { xs: "100%", sm: 280 }, maxWidth: "100%" }

/** Accumulate only explicitly requested pages, fenced by the search/parent key.
 * Never reuse RTK's `data`, which can belong to an earlier query argument. */
function useOptions<T extends { id: string }>(
  key: string,
  page: number,
  current?: Page<T>,
) {
  const [loaded, setLoaded] = useState<{ key: string; options: T[] }>({
    key,
    options: [],
  })
  useEffect(() => {
    if (!current || current.page !== page) return
    setLoaded((previous) => {
      const options = [
        ...new Map(
          [
            ...(previous.key === key && page > 1 ? previous.options : []),
            ...current.data,
          ].map((item) => [item.id, item]),
        ).values(),
      ]
      return previous.key === key && equal(previous.options, options)
        ? previous
        : { key, options }
    })
  }, [key, page, current])
  return loaded.key === key ? loaded.options : []
}

export function PublicationFilters({
  templateId,
  templateVersionId,
  onChange,
}: Filters & { onChange: (filters: Filters) => void }) {
  const [opened, setOpened] = useState(false)
  const [search, setSearch] = useState("")
  const [page, setPage] = useState(1)
  const [chosenTemplate, setChosenTemplate] = useState<{
    id: string
    name: string
  } | null>(null)
  const templates = useGetTemplatesQuery(
    opened ? { page, limit: 50, search: search || undefined } : skipToken,
  )
  const current = !templates.error ? templates.currentData : undefined
  const options = useOptions(search, page, current)
  const templateQuery = useGetTemplateQuery(templateId || skipToken)
  const versionQuery = useGetTemplateVersionQuery(
    templateVersionId || skipToken,
  )
  const version =
    !versionQuery.error && versionQuery.currentData?.id === templateVersionId
      ? versionQuery.currentData
      : undefined
  const template = templateId
    ? ((!templateQuery.error && templateQuery.currentData?.id === templateId
        ? templateQuery.currentData
        : undefined) ??
      (chosenTemplate?.id === templateId ? chosenTemplate : undefined) ??
      options.find((item) => item.id === templateId) ?? {
        id: templateId,
        name: `Шаблон ${templateId}`,
      })
    : null
  // Hydrate the catalog parent for a version-only deep link without silently
  // adding a template filter to the URL or changing the publications request.
  const versionParent = templateId || version?.templateId
  return (
    <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap">
      <Box sx={fieldSx}>
        <ResourceSelect
          size="small"
          label="Шаблон"
          value={template}
          options={templates.error ? [] : options}
          getOptionKey={(item) => item.id}
          getOptionLabel={(item) => item.name}
          onChange={(item) => {
            setChosenTemplate(item)
            onChange({
              templateId: item?.id,
              templateVersionId:
                item && version?.templateId === item.id
                  ? templateVersionId
                  : undefined,
            })
          }}
          onSearch={(value) => {
            setSearch(value)
            setPage(1)
          }}
          onOpen={() => setOpened(true)}
          hasMore={!!current && page < current.totalPages}
          isFetching={templates.isFetching}
          onLoadMore={() => {
            if (current && page < current.totalPages && !templates.isFetching)
              setPage(page + 1)
          }}
          error={templates.error}
          onRetry={() => void templates.refetch()}
        />
        {templateId && <TemplateQueryFeedback query={templateQuery} />}
      </Box>
      <Box sx={fieldSx}>
        <VersionFilter
          key={versionParent ?? "none"}
          templateId={versionParent}
          selectedId={templateVersionId}
          selected={version}
          onChange={(value) => onChange({ templateVersionId: value })}
        />
        {templateVersionId && <TemplateQueryFeedback query={versionQuery} />}
      </Box>
    </Stack>
  )
}

function VersionFilter({
  templateId,
  selectedId,
  selected,
  onChange,
}: {
  templateId?: string
  selectedId?: string
  selected?: TemplateVersion
  onChange: (value?: string) => void
}) {
  const [opened, setOpened] = useState(false)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState("")
  const query = useGetTemplateVersionsQuery(
    opened && templateId ? { templateId, page, limit: 50 } : skipToken,
  )
  const current = !query.error ? query.currentData : undefined
  const options = useOptions(templateId ?? "", page, current).filter(
    (item) => item.templateId === templateId,
  )
  const value = selectedId
    ? (selected ??
      options.find((item) => item.id === selectedId) ?? {
        id: selectedId,
        number: undefined,
      })
    : null
  return (
    <ResourceSelect
      size="small"
      label="Версия"
      value={value}
      options={
        query.error
          ? []
          : options.filter((item) => String(item.number).includes(search))
      }
      getOptionKey={(item) => item.id}
      getOptionLabel={(item) => `Версия ${item.number ?? item.id}`}
      onChange={(item) => onChange(item?.id)}
      onSearch={setSearch}
      onOpen={() => setOpened(true)}
      hasMore={!!current && page < current.totalPages}
      isFetching={query.isFetching}
      disabled={!templateId && !selectedId}
      onLoadMore={() => {
        if (current && page < current.totalPages && !query.isFetching)
          setPage(page + 1)
      }}
      error={query.error}
      onRetry={() => void query.refetch()}
    />
  )
}
